import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOfflineStatus } from './useOfflineStatus';

const CACHE_PREFIX = 'sorin_cache_';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

export function useCachedData<T>(
  key: string,
  fetchFn: () => Promise<T | null>,
  options: {
    ttl?: number;
    enabled?: boolean;
  } = {}
) {
  const { isOffline } = useOfflineStatus();
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);

  const cacheKey = `${CACHE_PREFIX}${key}`;
  const ttl = options.ttl ?? CACHE_TTL;
  const enabled = options.enabled ?? true;

  // Keep a ref to the latest fetchFn so the effect never needs it as a dep
  const fetchFnRef = useRef(fetchFn);
  useEffect(() => { fetchFnRef.current = fetchFn; });

  // Get from cache
  const getFromCache = useCallback((): T | null => {
    try {
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      const entry = JSON.parse(cached) as CacheEntry<T>;
      const now = Date.now();

      // Check if cache is still valid
      if (now - entry.timestamp > ttl) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      return entry.data;
    } catch {
      return null;
    }
  }, [cacheKey, ttl]);

  // Save to cache
  const saveToCache = useCallback((newData: T) => {
    try {
      const entry: CacheEntry<T> = {
        data: newData,
        timestamp: Date.now(),
      };
      localStorage.setItem(cacheKey, JSON.stringify(entry));
    } catch (e) {
      console.warn('Failed to cache data:', e);
    }
  }, [cacheKey]);

  // Clear cache
  const clearCache = useCallback(() => {
    localStorage.removeItem(cacheKey);
  }, [cacheKey]);

  // Fetch data - stable identity, calls fetchFnRef so it always has the latest fn
  const fetchData = useCallback(async () => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // If offline, use cache
      if (isOffline) {
        const cached = getFromCache();
        if (cached) {
          setData(cached);
          setIsFromCache(true);
          return;
        }
        throw new Error('No cached data available offline');
      }

      // Try to fetch fresh data using the ref so function identity is stable
      const freshData = await fetchFnRef.current();

      if (freshData) {
        setData(freshData);
        saveToCache(freshData);
        setIsFromCache(false);
      } else {
        // Fall back to cache if fetch returns null
        const cached = getFromCache();
        if (cached) {
          setData(cached);
          setIsFromCache(true);
        }
      }
    } catch (err: any) {
      console.error(`Failed to fetch ${key}:`, err);

      // On error, try cache
      const cached = getFromCache();
      if (cached) {
        setData(cached);
        setIsFromCache(true);
        setError(null); // Clear error if we have cached data
      } else {
        setError(err);
      }
    } finally {
      setIsLoading(false);
    }
    // fetchFnRef is intentionally excluded — it's a ref, never changes identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isOffline, getFromCache, saveToCache, key]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    isLoading,
    error,
    isFromCache,
    refresh: fetchData,
    clearCache,
  };
}

// Hook specifically for caching coworkers list
export function useCachedCoworkers(companyId: string | null, currentWorkerId: string | null, siteId?: string | null) {
  const fetchFn = useCallback(async () => {
    if (!companyId || !currentWorkerId) return null;

    let query = supabase
      .from('worker_profiles')
      .select('id, display_name, team')
      .eq('company_id', companyId)
      .neq('id', currentWorkerId);

    if (siteId) {
      query = query.eq('site_id', siteId);
    }

    const { data } = await query.limit(100);
    return data;
  }, [companyId, currentWorkerId, siteId]);

  return useCachedData(
    `coworkers_${companyId}_${siteId || 'all'}`,
    fetchFn,
    { enabled: !!companyId && !!currentWorkerId }
  );
}

// Hook for caching the last checkin location
export function useCachedCheckinLocation(workerId: string | null) {
  const fetchFn = useCallback(async () => {
    if (!workerId) return null;

    const today = new Date().toISOString().split('T')[0];
    const { data } = await supabase
      .from('shift_checkins')
      .select('work_location, shift_name, planned_tasks')
      .eq('worker_id', workerId)
      .eq('check_in_date', today)
      .maybeSingle();

    return data;
  }, [workerId]);

  return useCachedData(
    `last_checkin_${workerId}`,
    fetchFn,
    { enabled: !!workerId, ttl: 12 * 60 * 60 * 1000 } // 12 hour TTL for checkin
  );
}

// Hook for caching active quests
export function useCachedQuests(workerId: string | null) {
  return useCachedData(
    `quests_${workerId}`,
    async () => {
      if (!workerId) return null;

      const { data } = await supabase
        .from('hazard_recognition_quests')
        .select('*')
        .eq('worker_id', workerId)
        .eq('status', 'active')
        .order('assigned_at', { ascending: false })
        .limit(10);

      return data;
    },
    { enabled: !!workerId }
  );
}
